import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Info,
  Lock,
  Image as ImageIcon,
  UploadCloud,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  FileText,
  Eye,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link2,
} from "lucide-react";
import { IMAGE_ACCEPT, isImageFile } from "@/lib/uploadImage";
const FIXED_FOOTER =
  "If you have any questions or need to make changes to your booking, please contact us at customerservice@exqura.golf";

const TEMPLATE_DEFS = [
  {
    id: "booking_confirmation",
    label: "1. Payment & Booking Confirmation",
    trigger: "Sent automatically the moment payment succeeds.",
    fromLabel: "no-reply@exqura.golf",
    hasInstruction: false,
    hasImages: false,
    hasPdfNote: false,
    variables: [
      { key: "customerName", desc: "Customer's first name", required: true },
      { key: "bookingId", desc: "Booking reference ID", required: true },
      {
        key: "packageName",
        desc: "Name of the golf package booked",
        required: true,
      },
      { key: "bookingDate", desc: "Date of the booking", required: true },
      { key: "amountPaid", desc: "Total amount paid", required: true },
      { key: "paymentMethod", desc: "Payment method used", required: false },
    ],
    defaultSubject: "Your Exqura Golf Booking is Confirmed! – {{bookingId}}",
    defaultBody: `Hi {{customerName}},<br/><br/>
Thank you for booking with Exqura Golf! We're excited to confirm your reservation.<br/><br/>
<b>Booking Summary:</b><br/>
Package: {{packageName}}<br/>
Booking ID: {{bookingId}}<br/>
Date: {{bookingDate}}<br/>
Amount Paid: {{amountPaid}}<br/>
Payment Method: {{paymentMethod}}<br/><br/>
Our team will review your booking, and you'll receive your detailed itinerary and Golf Getaway Pass in a follow-up email once everything is confirmed on our end.<br/><br/>
Thanks for choosing Exqura Golf — we can't wait to have you out on the course!<br/><br/>

{{instructionMessage}}<br/><br/>

Warm regards,<br/>
Team Exqura Golf<br/><br/>
<i>${FIXED_FOOTER}</i>`,
  },
  {
    id: "itinerary_pass",
    label: "2. Itinerary + Golf Getaway Pass",
    trigger: "Manually triggered by admin once all bookings are confirmed.",
    fromLabel: "no-reply@exqura.golf",
    hasInstruction: true,
    hasImages: true,
    hasPdfNote: true,
    variables: [
      { key: "customerName", desc: "Customer's first name", required: true },
      { key: "bookingId", desc: "Booking reference ID", required: true },
      {
        key: "packageName",
        desc: "Name of the golf package booked",
        required: true,
      },
      { key: "travelDate", desc: "Date of travel", required: true },
      {
        key: "instructionMessage",
        desc: "The instruction text set below",
        required: true,
      },
    ],
    defaultSubject:
      "Your Itinerary & Golf Getaway Pass Are Here – {{bookingId}}",
    defaultBody: `Hi {{customerName}},<br/><br/>
Great news — your booking is fully confirmed! Please find attached your Golf Getaway Pass and complete itinerary for <b>{{packageName}}</b>.<br/><br/>
Booking ID: {{bookingId}}<br/>
Travel Date: {{travelDate}}<br/><br/>
{{instructionMessage}}<br/><br/>
Warm regards,<br/>
Team Exqura Golf<br/><br/>
<i>${FIXED_FOOTER}</i>`,
    defaultInstruction:
      "Need to reschedule? Just reach out to our team at customerservice@exqura.golf and we'll be happy to help.",
  },
  {
    id: "reminder_24h",
    label: "3. 24-Hour Reminder",
    trigger: "Sent automatically 24 hours before the booking date.",
    fromLabel: "no-reply@exqura.golf",
    hasInstruction: true,
    hasImages: false,
    hasPdfNote: false,
    variables: [
      { key: "customerName", desc: "Customer's first name", required: true },
      { key: "bookingId", desc: "Booking reference ID", required: true },
      {
        key: "packageName",
        desc: "Name of the golf package booked",
        required: true,
      },
      { key: "bookingDate", desc: "Date of the booking", required: true },
      {
        key: "departureTime",
        desc: "Scheduled departure time",
        required: true,
      },
    ],
    defaultSubject: "Reminder: Your Golf Getaway is Tomorrow! – {{bookingId}}",
    defaultBody: `Hi {{customerName}},<br/><br/>
This is a friendly reminder that your Exqura Golf booking is coming up in less than 24 hours!<br/><br/>
Package: {{packageName}}<br/>
Date: {{bookingDate}}<br/>
Departure Time: {{departureTime}}<br/><br/>
Please make sure you have your Golf Getaway Pass ready and arrive at least 30 minutes prior to departure.<br/><br/>
We're looking forward to seeing you!<br/><br/>
Warm regards,<br/>
Team Exqura Golf<br/><br/>
<i>${FIXED_FOOTER}</i>`,
  },
];

const SAMPLE_DATA = {
  customerName: "Alex",
  bookingId: "EXQ-10482",
  packageName: "Batam Golf Getaway – 2D1N",
  bookingDate: "August 20, 2026",
  travelDate: "August 20, 2026",
  departureTime: "8:30 AM",
  amountPaid: "SGD 540.00",
  paymentMethod: "Visa •••• 4242",
  instructionMessage:
    "Need to reschedule? Just reach out to our team at customerservice@exqura.golf and we'll be happy to help.",
};

// Text color swatches and size presets for the rich text toolbar. Both
// produce plain inline-styled HTML (no custom tags), so they render
// identically wherever the saved template.body ends up — in the live
// preview here, and in the actual email once Brevo sends the raw HTML.
const TEXT_COLORS = [
  { label: "Black", hex: "#111827" },
  { label: "Red", hex: "#DC2626" },
  { label: "Blue", hex: "#2563EB" },
  { label: "Green", hex: "#16A34A" },
  { label: "Yellow", hex: "#CA8A04" },
];

const TEXT_SIZES = [
  { label: "S", px: 12, title: "Small" },
  { label: "N", px: 14, title: "Normal" },
  { label: "L", px: 18, title: "Large" },
];

function findVariableToken(key) {
  return `{{${key}}}`;
}

function renderWithSampleData(text) {
  if (!text) return "";
  return text.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, key) =>
    SAMPLE_DATA[key] !== undefined ? SAMPLE_DATA[key] : match,
  );
}

function missingRequiredVariables(def, subject, body) {
  const haystack = `${subject}\n${body}`;
  return def.variables
    .filter((v) => v.required)
    .filter((v) => !haystack.includes(findVariableToken(v.key)))
    .map((v) => v.key);
}

// Normalizes each image to { url, caption } — older saved templates stored
// images as plain URL strings before captions existed, so those are upgraded
// on load rather than breaking the UI (buildGolfPassDoc's normalizeImages
// already accepts both shapes, so this doesn't affect the PDF side).
function normalizeImageItem(img) {
  return typeof img === "string" ? { url: img, caption: "" } : img;
}

function templateToState(def, saved) {
  return {
    subject: saved?.subject ?? def.defaultSubject,
    body: saved?.body ?? def.defaultBody,
    instruction: saved?.instruction ?? def.defaultInstruction ?? "",
    images: Array.isArray(saved?.images)
      ? saved.images.map(normalizeImageItem)
      : [],
  };
}

/**
 * @param {{
 *   onFetchTemplates: () => Promise<Record<string, any>>; // { [templateId]: { subject, body, instruction, images } }
 *   onSaveTemplate: (templateId: string, payload: any) => Promise<any>;
 *   onUploadImage: (file: File) => Promise<string>;
 * }} props
 */
export default function CreateEmailTemplateAdmin({
  onFetchTemplates,
  onSaveTemplate,
  onUploadImage,
}) {
  const [activeId, setActiveId] = useState(TEMPLATE_DEFS[0].id);
  const [templates, setTemplates] = useState(() =>
    Object.fromEntries(
      TEMPLATE_DEFS.map((d) => [d.id, templateToState(d, null)]),
    ),
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const lastFocusedRef = useRef("body"); // "subject" | "body"

  const activeDef = TEMPLATE_DEFS.find((d) => d.id === activeId);
  const activeState = templates[activeId];

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const saved = await onFetchTemplates();
      setTemplates(
        Object.fromEntries(
          TEMPLATE_DEFS.map((d) => [d.id, templateToState(d, saved?.[d.id])]),
        ),
      );
    } catch (err) {
      console.error("Failed to load email templates:", err);
      setLoadError(err?.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, [onFetchTemplates]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const switchTab = (id) => {
    setActiveId(id);
    setSaveError(null);
    setSaveSuccess(false);
    setValidationError(null);
  };

  const setActiveField = (field, value) => {
    setTemplates((prev) => ({
      ...prev,
      [activeId]: { ...prev[activeId], [field]: value },
    }));
    setSaveSuccess(false);
  };

  const insertVariable = (key) => {
    const token = findVariableToken(key);

    // Body is a contentEditable rich text area now — insert via execCommand so
    // it lands exactly at the cursor and plays nicely with bold/italic runs.
    if (lastFocusedRef.current === "body" && bodyRef.current) {
      if (document.activeElement !== bodyRef.current) bodyRef.current.focus();
      document.execCommand("insertText", false, token);
      setActiveField("body", bodyRef.current.innerHTML);
      return;
    }

    const target = subjectRef.current;
    const current = activeState.subject || "";
    if (target && typeof target.selectionStart === "number") {
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const next = current.slice(0, start) + token + current.slice(end);
      setActiveField("subject", next);
      requestAnimationFrame(() => {
        target.focus();
        const pos = start + token.length;
        target.setSelectionRange(pos, pos);
      });
    } else {
      setActiveField("subject", current + token);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    const missing = missingRequiredVariables(
      activeDef,
      activeState.subject,
      activeState.body,
    );
    if (missing.length > 0) {
      setValidationError(
        `This template is missing required variable(s): ${missing
          .map((k) => `{{${k}}}`)
          .join(
            ", ",
          )}. These get replaced with real booking data, so they can't be removed.`,
      );
      return;
    }
    setValidationError(null);

    const payload = {
      subject: activeState.subject.trim(),
      body: activeState.body,
      ...(activeDef.hasInstruction
        ? { instruction: activeState.instruction.trim() }
        : {}),
      ...(activeDef.hasImages ? { images: activeState.images } : {}),
    };

    setSaving(true);
    try {
      await onSaveTemplate(activeId, payload);
      setSaveSuccess(true);
    } catch (err) {
      console.error("Failed to save email template:", err);
      setSaveError(
        err?.message || "Failed to save template. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    const confirmReset = window.confirm(
      `Reset "${activeDef.label}" back to the default pre-filled template? Unsaved changes will be lost.`,
    );
    if (!confirmReset) return;
    setTemplates((prev) => ({
      ...prev,
      [activeId]: templateToState(activeDef, null),
    }));
    setValidationError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8F7] flex items-center justify-center gap-2 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading email templates...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-[#0B2421] flex items-center gap-2">
            <Mail className="h-7 w-7" />
            Email Templates
          </h1>
          <p className="text-gray-500 mt-1">
            Edit the subject and content of each booking email. Fields
            highlighted below are filled in automatically per customer and can't
            be deleted from the text.
          </p>
        </motion.div>

        {loadError && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{loadError}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TEMPLATE_DEFS.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => switchTab(d.id)}
              className={`h-11 px-5 rounded-xl text-sm font-semibold transition-colors border ${
                activeId === d.id
                  ? "bg-[#0B2421] text-white border-[#0B2421]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-[#0B2421]/40"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        <div className="mb-6 flex items-start gap-2 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-3 text-sm text-[#7a5f13]">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{activeDef.trigger}</span>
        </div>

        {saveSuccess && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            Template saved successfully.
          </div>
        )}
        {saveError && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{saveError}</span>
          </div>
        )}
        {validationError && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-6">
            <Section title="Available Variables" icon={Lock}>
              <p className="text-xs text-gray-400 mb-3">
                Click a variable to insert it wherever your cursor is in Subject
                or Content below. Fields marked required must stay somewhere in
                the email.
              </p>
              <div className="flex flex-wrap gap-2">
                {activeDef.variables.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    title={v.desc}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertVariable(v.key)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0B2421]/5 hover:bg-[#0B2421]/10 border border-[#0B2421]/15 px-3 py-1.5 text-xs font-semibold text-[#0B2421] transition-colors"
                  >
                    <Lock className="h-3 w-3 opacity-50" />
                    {`{{${v.key}}}`}
                    {v.required && <span className="text-[#D4AF37]">*</span>}
                  </button>
                ))}
              </div>
            </Section>

            <Section title="Email Content">
              <div className="space-y-4">
                <Field label="Subject Line" required>
                  <input
                    ref={subjectRef}
                    type="text"
                    value={activeState.subject}
                    onChange={(e) => setActiveField("subject", e.target.value)}
                    onFocus={() => (lastFocusedRef.current = "subject")}
                    className={inputClass(false)}
                  />
                </Field>

                <Field label="Body Content" required>
                  <RichTextEditor
                    editorRef={bodyRef}
                    value={activeState.body}
                    onChange={(html) => setActiveField("body", html)}
                    onFocus={() => (lastFocusedRef.current = "body")}
                  />
                  <p className="mt-1.5 text-xs text-gray-400">
                    The support line at the bottom is regular text now — reword
                    it or move it anywhere in the email. Just don't delete the
                    required {`{{variables}}`} above.
                  </p>
                </Field>
              </div>
            </Section>

            {activeDef.hasInstruction && (
              <Section title="Instruction Message" icon={Info}>
                <Field label="Instruction Text" required>
                  <textarea
                    value={activeState.instruction}
                    onChange={(e) =>
                      setActiveField("instruction", e.target.value)
                    }
                    rows={3}
                    placeholder="e.g. Need to reschedule? Contact us at customerservice@exqura.golf"
                    className={`${inputClass(false)} h-auto py-3 resize-none`}
                  />
                  <p className="mt-1.5 text-xs text-gray-400">
                    Shown in place of the {`{{instructionMessage}}`} variable.
                    Admin can update this each time before sending.
                  </p>
                </Field>
              </Section>
            )}

            {activeDef.hasImages && (
              <Section title="Attached Images (up to 5)" icon={ImageIcon}>
                <MultiImageUpload
                  images={activeState.images}
                  onChange={(imgs) => setActiveField("images", imgs)}
                  onUploadImage={onUploadImage}
                  max={5}
                />
              </Section>
            )}

            {activeDef.hasPdfNote && (
              <Section
                title="Golf Getaway Pass (PDF Attachment)"
                icon={FileText}
              >
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">
                    This email will also carry the customer's Golf Getaway Pass
                    as a PDF attachment. The pass generation/attachment logic
                    isn't wired up yet — flagged for a separate discussion
                    before this template goes live.
                  </p>
                </div>
              </Section>
            )}

            <div className="flex items-center gap-3 justify-end pb-4">
              <button
                type="button"
                onClick={handleResetToDefault}
                className="h-12 px-5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
              >
                Reset to Default
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="h-12 px-8 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Template"
                )}
              </button>
            </div>
          </div>

          <div className="hidden lg:block lg:sticky lg:top-8">
            <EmailPreview def={activeDef} state={activeState} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- rich text editor (bold / italic / underline / lists / links / size / color) ---------- */

function RichTextEditor({ editorRef, value, onChange, onFocus }) {
  const savedRangeRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value, editorRef]);

  const focusEditor = () => {
    if (document.activeElement !== editorRef.current)
      editorRef.current?.focus();
  };

  const applyFormat = (command) => {
    if (!editorRef.current) return;
    focusEditor();
    document.execCommand(command);
    onChange(editorRef.current.innerHTML);
  };

  const applyBulletList = () => {
    if (!editorRef.current) return;
    focusEditor();
    document.execCommand("insertUnorderedList");
    onChange(editorRef.current.innerHTML);
  };

  const applyNumberedList = () => {
    if (!editorRef.current) return;
    focusEditor();
    document.execCommand("insertOrderedList");
    onChange(editorRef.current.innerHTML);
  };

  // execCommand("fontSize") only supports the legacy 1-7 <font size> scale,
  // not real px values -- so it's used purely as a selection-aware wrapper
  // (browsers handle multi-node selections correctly here, which a manual
  // Range.surroundContents() call does not), then immediately swapped for a
  // proper inline-styled <span>, which is what actually ends up in the HTML
  // that gets saved and sent through Brevo.
  const applyFontSize = (px) => {
    if (!editorRef.current) return;
    focusEditor();
    document.execCommand("fontSize", false, "7");
    editorRef.current.querySelectorAll('font[size="7"]').forEach((el) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      span.innerHTML = el.innerHTML;
      el.replaceWith(span);
    });
    onChange(editorRef.current.innerHTML);
  };

  const applyColor = (hex) => {
    if (!editorRef.current) return;
    focusEditor();
    document.execCommand("foreColor", false, hex);
    onChange(editorRef.current.innerHTML);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (
      sel &&
      sel.rangeCount > 0 &&
      editorRef.current?.contains(sel.anchorNode)
    ) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  };

  const applyLink = () => {
    if (!editorRef.current) return;
    focusEditor();

    const sel = window.getSelection();
    const hasSelection = sel && sel.toString().trim().length > 0;
    if (!hasSelection) {
      window.alert("Highlight the text you want to turn into a link first.");
      return;
    }
    saveSelection();

    const input = window.prompt(
      "Enter a web address (https://...) or an email address for a mailto link:",
      "customerservice@exqura.golf",
    );
    if (!input) return;

    const trimmed = input.trim();
    let href = trimmed;
    const isEmail =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) &&
      !trimmed.toLowerCase().startsWith("mailto:");

    if (isEmail) {
      href = `mailto:${trimmed}`;
    } else if (
      !trimmed.toLowerCase().startsWith("mailto:") &&
      !/^https?:\/\//i.test(trimmed)
    ) {
      href = `https://${trimmed}`;
    }

    restoreSelection();
    document.execCommand("createLink", false, href);
    onChange(editorRef.current.innerHTML);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 mb-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
        <RichTextButton
          icon={Bold}
          label="Bold"
          onClick={() => applyFormat("bold")}
        />
        <RichTextButton
          icon={Italic}
          label="Italic"
          onClick={() => applyFormat("italic")}
        />
        <RichTextButton
          icon={Underline}
          label="Underline"
          onClick={() => applyFormat("underline")}
        />
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        <RichTextButton
          icon={List}
          label="Bullet points"
          onClick={applyBulletList}
        />
        <RichTextButton
          icon={ListOrdered}
          label="Numbered list"
          onClick={applyNumberedList}
        />
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        <RichTextButton
          icon={Link2}
          label="Insert link (URL or mailto)"
          onClick={applyLink}
        />
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        {TEXT_SIZES.map((s) => (
          <button
            key={s.label}
            type="button"
            title={s.title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFontSize(s.px)}
            className="h-8 w-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-white hover:text-[#0B2421] transition-colors font-semibold"
            style={{ fontSize: `${Math.min(s.px, 16)}px` }}
          >
            A
          </button>
        ))}
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        {TEXT_COLORS.map((c) => (
          <button
            key={c.hex}
            type="button"
            title={c.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyColor(c.hex)}
            className="h-6 w-6 rounded-full border border-gray-200 hover:scale-110 transition-transform"
            style={{ backgroundColor: c.hex }}
          />
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onFocus={onFocus}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="w-full min-h-[300px] rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_a]:underline [&_a]:text-[#0B2421] [&_a]:font-medium"
      />
    </div>
  );
}

function RichTextButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="h-8 w-8 rounded-md flex items-center justify-center text-gray-500 hover:bg-white hover:text-[#0B2421] transition-colors"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/* ---------- live preview ---------- */

function EmailPreview({ def, state }) {
  const subject = renderWithSampleData(state.subject);
  const bodyHtml = renderWithSampleData(state.body).replace(
    "{{instructionMessage}}",
    state.instruction || "",
  );

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-[#0B2421] flex items-center gap-2">
        <Eye className="h-3.5 w-3.5 text-[#D4AF37]" />
        <span className="text-xs font-semibold tracking-wide text-[#D4AF37] uppercase">
          Preview (with sample data)
        </span>
      </div>
      <div className="p-5">
        <div className="text-xs text-gray-400 mb-1">From: {def.fromLabel}</div>
        <div className="text-xs text-gray-400 mb-3">
          To: customer@example.com
        </div>
        <div className="text-sm font-bold text-[#0B2421] mb-4 pb-3 border-b border-gray-100">
          {subject || "Subject line"}
        </div>
        <div
          className="text-sm text-gray-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_a]:underline [&_a]:text-[#0B2421] [&_a]:font-medium"
          dangerouslySetInnerHTML={{ __html: bodyHtml || "Email body" }}
        />

        {def.hasImages && state.images.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-4">
            {state.images.map((img, i) => (
              <div key={i}>
                <img
                  src={img.url}
                  alt=""
                  className="w-full h-16 object-cover rounded-lg border border-gray-100"
                />
                {img.caption?.trim() && (
                  <p className="text-[10px] text-gray-500 text-center mt-1 truncate">
                    {img.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {def.hasPdfNote && (
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500">
            <FileText className="h-3.5 w-3.5" />
            golf-getaway-pass.pdf (attached)
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- multi image upload (up to N images), each with its own caption ---------- */

// images: Array<{ url: string, caption: string }>. `caption` shows under
// this photo in the "Important Visuals" gallery of the generated PDF
// (buildGolfPassDoc reads it straight off each image object).
function MultiImageUpload({ images, onChange, onUploadImage, max = 5 }) {
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((f) =>
        isImageFile(f),
      );
      if (files.length === 0) return;

      const slotsLeft = max - images.length;
      if (slotsLeft <= 0) {
        setLocalError(`You can only attach up to ${max} images.`);
        return;
      }

      setLocalError(null);
      setIsUploading(true);
      try {
        const toUpload = files.slice(0, slotsLeft);
        const urls = await Promise.all(toUpload.map((f) => onUploadImage(f)));
        const newItems = urls.map((url) => ({ url, caption: "" }));
        onChange([...images, ...newItems]);
      } catch (err) {
        console.error("Image upload failed:", err);
        setLocalError(err?.message || "Upload failed. Try again.");
      } finally {
        setIsUploading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [images, max, onChange, onUploadImage],
  );

  const removeAt = (index) => onChange(images.filter((_, i) => i !== index));

  const updateCaption = (index, caption) =>
    onChange(
      images.map((img, i) => (i === index ? { ...img, caption } : img)),
    );

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
        {images.map((img, i) => (
          <div
            key={i}
            className="rounded-xl overflow-hidden border border-gray-200"
          >
            <div className="relative group aspect-square">
              <img src={img.url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <input
              type="text"
              value={img.caption || ""}
              onChange={(e) => updateCaption(i, e.target.value)}
              placeholder="Caption for this photo"
              maxLength={60}
              className="w-full text-xs px-2.5 py-2 border-t border-gray-100 bg-white focus:outline-none focus:ring-1 focus:ring-[#0B2421]/30"
            />
          </div>
        ))}

        {images.length < max && (
          <label
            className={`flex flex-col items-center justify-center aspect-square rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
              localError
                ? "border-red-300 bg-red-50"
                : "border-gray-200 bg-gray-50 hover:bg-gray-100"
            }`}
          >
            {isUploading ? (
              <Loader2 className="h-5 w-5 text-[#0B2421] animate-spin" />
            ) : (
              <>
                <UploadCloud className="h-5 w-5 text-gray-400 mb-1" />
                <span className="text-[10px] text-gray-400">Add</span>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={IMAGE_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </label>
        )}
      </div>
      <p className="text-xs text-gray-400">
        {images.length}/{max} images added. Captions are optional — each one
        shows under its own photo in the Golf Getaway Pass PDF.
      </p>
      {localError && <FieldError message={localError} />}
    </div>
  );
}

/* ---------- shared layout primitives (same visual language as CreateFerriesAdmin) ---------- */

function Section({ title, icon: Icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm"
    >
      <h2 className="text-lg font-bold mb-4 text-[#0B2421] flex items-center">
        {Icon && <Icon className="h-5 w-5 mr-2" />}
        {title}
      </h2>
      {children}
    </motion.div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
      {children} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      {children}
      {error && <FieldError message={error} />}
    </div>
  );
}

function inputClass(hasError) {
  return `w-full h-12 rounded-xl border px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 ${
    hasError ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
  }`;
}

function FieldError({ message }) {
  return (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />
      {message}
    </p>
  );
}